import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function MoyensPaiement() {
  const [moyens, setMoyens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [typesPaiement, setTypesPaiement] = useState([]);
  const [formData, setFormData] = useState({
    institution: '',
    type_paiement: '',
    source_qr: '',
    qr_image_base64: '',
    qr_image_preview: null
  });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchMoyens = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/get_moyens_paiement.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const moyensNormalises = (data.moyens || []).map(mp => ({
          ...mp,
          qr_image_url: config.normalizeQrCode(mp.qr_image_url)
        }));
        setMoyens(moyensNormalises);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/get_institutions.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setInstitutions(data.institutions);
    } catch (error) {
      console.error('Erreur institutions:', error);
    }
  };

  const fetchTypesPaiement = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/get_types_paiement.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setTypesPaiement(data.types_paiement);
    } catch (error) {
      console.error('Erreur types paiement:', error);
      setTypesPaiement(['Orange Money', 'Moov Money', 'Airtel Money', 'MTN Mobile Money', 'Wave', 'TMoney']);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop volumineuse (max 5 Mo)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          qr_image_base64: reader.result,
          qr_image_preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.institution) {
      toast.error('Sélectionnez votre institution');
      return;
    }
    if (!formData.type_paiement) {
      toast.error('Sélectionnez le type de paiement');
      return;
    }
    if (!formData.source_qr.trim()) {
      toast.error('Entrez le numéro de téléphone');
      return;
    }
    if (!formData.qr_image_base64) {
      toast.error('Sélectionnez l\'image QR');
      return;
    }
    setSubmitting(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/add_moyen_paiement.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Moyen de paiement ajouté');
        setShowForm(false);
        setFormData({
          institution: '',
          type_paiement: '',
          source_qr: '',
          qr_image_base64: '',
          qr_image_preview: null
        });
        fetchMoyens();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatut = async (id_mp, currentStatut) => {
    const newStatut = currentStatut === 'actif' ? 'inactif' : 'actif';
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/toggle_statut_paiement.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_mp, statut: newStatut })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Moyen ${newStatut === 'actif' ? 'activé' : 'désactivé'}`);
        fetchMoyens();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteMoyen = async (id_mp) => {
    if (!confirm('Supprimer ce moyen de paiement ?')) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/pat/moyens_paiement/delete_moyen_paiement.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_mp })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Supprimé');
        fetchMoyens();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  useEffect(() => {
    fetchMoyens();
    fetchInstitutions();
    fetchTypesPaiement();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">💳 Moyens de paiement</h1>
          <p className="text-sm text-gray-500">Ajoutez vos comptes mobile money via QR code</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          {showForm ? '✕ Annuler' : '+ Ajouter'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border">
          <h2 className="text-lg font-bold mb-4">➕ Nouveau moyen de paiement</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Institution (votre lieu de travail) *</label>
              <select
                value={formData.institution}
                onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionnez votre institution...</option>
                {institutions.map(inst => (
                  <option key={inst} value={inst}>{inst}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type de paiement *</label>
              <select
                value={formData.type_paiement}
                onChange={(e) => setFormData({ ...formData, type_paiement: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionnez le type...</option>
                {typesPaiement.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Numéro de téléphone *</label>
              <input
                type="tel"
                placeholder="Ex: 90 00 00 00"
                value={formData.source_qr}
                onChange={(e) => setFormData({ ...formData, source_qr: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-400 mt-1">Le numéro associé à ce compte mobile money</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">QR Code *</label>
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.qr_image_preview ? (
                  <div>
                    <img src={formData.qr_image_preview} alt="QR" className="mx-auto max-h-32 rounded" />
                    <p className="text-sm text-green-600 mt-2">✓ Image chargée</p>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl mb-2">📱</div>
                    <p className="text-gray-500">Cliquez pour importer le QR code</p>
                    <p className="text-xs text-gray-400">JPEG, PNG, WEBP (max 5 Mo)</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Ajout...' : 'Ajouter'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {moyens.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-5xl mb-4">💳</div>
          <p className="text-gray-500">Aucun moyen de paiement</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-3 text-blue-600 hover:underline"
          >
            Ajoutez votre premier moyen de paiement
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {moyens.map(mp => (
            <div key={mp.id_mp} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition border">
              <div className="relative">
                <img src={mp.qr_image_url} alt="QR Code" className="w-full h-48 object-contain bg-gray-50 p-4" />
                <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                  mp.statut === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {mp.statut === 'actif' ? '✓ Actif' : '⏸ Inactif'}
                </span>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-800">{mp.type_paiement}</h3>
                    <p className="text-sm text-gray-500 mt-1">🏢 {mp.institution}</p>
                    <p className="text-sm text-gray-600 mt-1">📞 {mp.source_qr}</p>
                    <p className="text-xs text-gray-400 mt-2">Ajouté le {new Date(mp.date_creation).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleStatut(mp.id_mp, mp.statut)}
                      className={`text-sm px-2 py-1 rounded ${mp.statut === 'actif' ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                    >
                      {mp.statut === 'actif' ? '🔘 Désactiver' : '🟢 Activer'}
                    </button>
                    <button
                      onClick={() => deleteMoyen(mp.id_mp)}
                      className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}